<template>
    <Dialog :visible="showDialog" class="action-popup action2-popup" :modal="true" :draggable="false">
        <template #container>
            <div class="dialog-header-danger">
                Force Release a License
            </div>
            <div class="dialog-content align-items-center">
                <div class="w-full flex justify-content-center align-items-center mb-3">
                    <SvgWarning className="popup-warning-svg" color="var(--popup-warning-color)"/>
                </div>
                <span class="text mb-3 px-1 flex flex-column align-items-center text-center gap-1">
                    <span>
                        'Force Release' a license only if you don’t have access to the computer. To properly 'release' a license, check for instructions in the platform guide or click on the “?” below.
                    </span> 
                    <span>Note that if the Mac
                        "<span style="font-weight: var(--font-weight-normal);">{{ license.current_status.activeHardware.label }}</span>"
                        is connected to the internet,
                        <span style="font-weight: var(--font-weight-normal); color: var(--mydanger-color);">the application will immediately go to DEMO mode</span>.
                    </span>
                    <span>
                        Please be aware that frequent use of this operation may lead to your account being flagged for review.
                    </span>
                </span>

                <div class="content flex flex-column w-full justify-content-left mt-2">
                    <DataTable :value="[license]" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-2" style="max-height: 25vh;">
                        <Column field="serial_number" header="Serial number">
                            <template #body="slotProps">
                                <span class="text-serial-number">{{ slotProps.data.serial_number }}</span>
                            </template>
                        </Column>
                        <Column field="product.application" header="Application" style="max-width: 30%">
                            <template #body="slotProps">
                                <span>{{ isWebView && applicationGlobalInfo.name ?  applicationGlobalInfo.name : slotProps.data.product.application ?? "/" }}{{ slotProps.data.current_status.application_instance_id && slotProps.data.current_status.application_instance_id>0 ? ` ${slotProps.data.current_status.application_instance_id}` : "" }}</span>
                            </template>
                        </Column>
                        <Column header="Device">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.current_status.activeHardware">{{ slotProps.data.current_status.activeHardware.label ?? "/" }}</span>
                                <span v-else class="font-italic">/</span>
                            </template>
                        </Column>
                        <Column header="User">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.current_status.activeUser">{{ slotProps.data.current_status.activeUser.first_name ?? "" }} {{ slotProps.data.current_status.activeUser.last_name ?? "" }}</span>
                                <span v-else class="font-italic">/</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <div class="mt-4 flex flex-column text w-full align-items-center gap-2">
                    <span class="text-center px-2 sm:px-6">To confirm that you want to do a 'Force Release', please indicate the reason below:</span>

                    <div class="flex flex-column align-items-center justify-content-center gap-1" style="width: 85%; gap: 0.1rem;">
                        <div class="flex flex-column sm:flex-row align-items-center justify-content-center gap-3" style="width: 100%;">
                            <Dropdown class="align-self-start" :class="{'w-full': !(selectedReason && selectedReason.code == 'ANOTHER_REASON')}" v-model="selectedReason" :options="optionsReasons" optionLabel="abbrev" placeholder="Select a reason">
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex flex-row align-items-center h-full">
                                        <div v-if="slotProps.value" class="potentially-long-label">{{ slotProps.value.text }}</div>
                                    </div>
                                    <div v-else  class="flex flex-row align-items-center h-full">
                                        <div class="potentially-long-label">Select a reason</div>    
                                    </div>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex flex-row align-items-center">
                                        <div v-if="slotProps.option">{{ slotProps.option.text }}</div>
                                    </div>
                                </template>
                            </Dropdown>
                            <InputText class="w-full" v-if="selectedReason && selectedReason.code == 'ANOTHER_REASON'" v-model="customReason" aria-describedby="admin-help" placeholder="Insert the reason here..." :class="{ 'p-invalid': reasonError && (customReason.length == 0 || customReason.length > 256) }" autofocus/>
                        </div>
                        <span v-if="reasonError" class="error-message mb-2 align-self-start" style="color: var(--inputcomponent-invalid-color);">{{ reasonError }}</span>
                    </div>
                    
                </div>
            </div>

            <div class="dialog-footer">
                <a  class="justify-self-start flex align-items-center justify-content-center" :href="forceReleaseZendeskLink" target="_blank" @click="clickedZendeskLink">
                    <Button rounded text style="border-radius: 980px; padding: 0;">
                        <SvgQuestionMarkCircle className="info-svg" color="var(--button-normal-color)"/>
                    </Button>
                </a>
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="$emit('close')"/>
                    <ButtonModule type="primary" severity="danger" label="Force Release" @click="clickedForceRelease"/>
                </div>
            </div>
        </template>
    </Dialog>

</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, computed, watchEffect } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
// vue
import ButtonModule from '@/components/Modules/ButtonModule.vue'
import SvgWarning from '@/components/SVGs/SvgWarning.vue';
import SvgQuestionMarkCircle from '@/components/SVGs/SvgQuestionMarkCircle.vue';
// Others
import toastService from '@/composables/toastService';
import { useStore } from 'vuex';

const store = useStore()

// --- isWebView info ---
const isWebView = computed(() => store.state.applicationWebView.isWebView)
const applicationGlobalInfo = computed(() => store.state.applicationWebView.applicationGlobalInfo)

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    license: Object,
})
const { showDialog, license } = toRefs(props)

// --- EMITS ---
const emit = defineEmits(['close', 'force-release'])

// --- REASON ---
const optionsReasons = ref([
    { code: 'REFORMAT', abbrev: 'REFORMAT', text: 'I reformatted my computer and forgot to release the serial before.' },
    { code: 'COMPUTER_BROKEN', abbrev: 'BROKEN', text: 'I don’t have access to the computer as it is broken.' },
    { code: 'COMPUTER_LOST', abbrev: 'LOST', text: 'I don’t have access to the computer as it was lost.' },
    { code: 'COMPUTER_STOLEN', abbrev: 'STOLEN', text: 'I don’t have access to the computer as it was stolen.' },
    { code: 'COMPUTER_REPAIRED', abbrev: 'REPAIRED', text: 'The computer was repaired and reformatted during the repair.' },
    { code: 'ANOTHER_REASON', abbrev: 'OTHER', text: 'Another reason:' }
]);
const selectedReason = ref(null)
const customReason = ref('')
const reasonError = ref(null)

// Whenever the dialog opens, it should reset the input reason
watchEffect(() => {
    if (showDialog.value) {
        selectedReason.value = null
        customReason.value = ''
    }
})

// --- Support link ---
const forceReleaseZendeskLink = "https://softron.zendesk.com/hc/en-us/sections/14666349935516-Softron-License-Manager-New-2024-Activations"
const clickedZendeskLink = () => {
    // (in case it is in the web view in the app, ask the app to open it also, because the href thing will not work if it is in the web view)
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
        window.webkit.messageHandlers.jsOpenWebPage.postMessage(forceReleaseZendeskLink);
    }
}

// --- Force Release confirmation ---
const clickedForceRelease = () => {
    if (!selectedReason.value) {
        toastService.add({ severity: 'error', summary: "Force Release: Reason", detail: 'Please select a reason.', life: 5000 });
        reasonError.value = "Please indicate a reason for the 'Force Release' to confirm you understood the implications."
        return;
    }
    if (selectedReason.value.code != 'ANOTHER_REASON') { // We selected a pre-defined reason
        // toastService.add({ severity: 'success', summary: "Force Release: Success", detail: 'Force Release request will be sent.', life: 5000 });
        reasonError.value = null
        emit('force-release', license.value.serial_number, selectedReason.value.code, customReason.value)
        return;
    }
    // If we are here, it means that we selected the customized reason
    if (customReason.value.length > 255) {
        toastService.add({ severity: 'error', summary: "Force Release: Reason", detail: 'Please make the reason shorter.', life: 5000 });
        reasonError.value = "Exceeded the 255-character limit. Please reduce the text."
        return;
    }
    if (customReason.value.length == 0) {
        toastService.add({ severity: 'error', summary: "Force Release: Reason", detail: 'Please fill the reason.', life: 5000 });
        reasonError.value = "Please indicate a reason for the 'Force Release' to confirm you understood the implications."
        return;
    }
    // If we are here, it means that we selected the customized reason which is valid in terms of length
    // toastService.add({ severity: 'success', summary: "Force Release: Success", detail: 'Force Release request will be sent. (Customized reason)', life: 5000 });
    reasonError.value = null
    emit('force-release', license.value.serial_number, selectedReason.value.code, customReason.value)
}
</script>

<style scoped>
/* Dropdown style */
:deep(.p-dropdown) {
    width: 100%;
    height: 2.1rem;
}
:deep(.p-dropdown-label), :deep(.p-dropdown-item-label:not(.p-highlight .p-dropdown-item-label)), :deep(.p-dropdown .p-icon) {
    color: var(--theme-font-color);
    opacity: 100%;
}
</style>